import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { readEdgeError } from "@/lib/edgeError";

// Shape returned by the business-data-connections edge function. auth_credentials
// is REDACTED to "***" (or null). Never the real values.
export interface BusinessDataConnection {
  id: string;
  business_id: string;
  name: string;
  description: string;
  is_active: boolean;
  connection_type: "url_feed" | "rest_api" | "rest_action" | "mcp_remote";
  // rest_action only: how the AI may run it, and the post-action confirmation.
  action_mode?: "auto" | "ask" | "off" | null;
  success_message?: string | null;
  endpoint_url: string;
  http_method: "GET" | "POST";
  request_body_template: unknown;
  auth_type: "none" | "bearer" | "header" | "basic" | "query";
  auth_credentials: null | "***";
  refresh_interval_seconds: number;
  last_fetched_at: string | null;
  last_payload: unknown;
  last_payload_size_bytes: number | null;
  last_error: string | null;
  last_error_at: string | null;
  argument_schema: unknown;
  created_at: string;
  updated_at: string;
}

export interface CreateConnectionInput {
  name: string;
  description: string;
  connection_type: "url_feed" | "rest_api" | "rest_action" | "mcp_remote";
  // action_mode + success_message apply to the gated types (rest_action AND
  // mcp_remote tool calls).
  action_mode?: "auto" | "ask" | "off";
  success_message?: string | null;
  endpoint_url: string;
  http_method?: "GET" | "POST";
  request_body_template?: unknown;
  auth_type: "none" | "bearer" | "header" | "basic" | "query";
  auth_credentials?: Record<string, string> | null;
  refresh_interval_seconds?: number;
  argument_schema?: unknown;
}

async function invoke(body: Record<string, unknown>): Promise<any> {
  const { data, error } = await supabase.functions.invoke("business-data-connections", { body });
  if (error) throw new Error(await readEdgeError(error, "Request failed"));
  if (data?.error) throw new Error(data.error);
  return data;
}

export function useBusinessDataConnections() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ["businessDataConnections", user?.id];

  const { data: connections = [], isLoading } = useQuery({
    queryKey,
    enabled: !!user,
    staleTime: 15_000,
    queryFn: async (): Promise<BusinessDataConnection[]> => {
      const res = await invoke({ action: "list" });
      return res.connections ?? [];
    },
  });

  const refetch = () => queryClient.invalidateQueries({ queryKey });

  const create = useMutation({
    mutationFn: (input: CreateConnectionInput) => invoke({ action: "create", ...input }),
    onSuccess: () => {
      toast.success("Data source connected.");
      refetch();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const update = useMutation({
    mutationFn: (input: { id: string } & Partial<CreateConnectionInput>) =>
      invoke({ action: "update", ...input }),
    onSuccess: () => {
      toast.success("Data source updated.");
      refetch();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const toggle = useMutation({
    mutationFn: (id: string) => invoke({ action: "toggle", id }),
    onSuccess: () => refetch(),
    onError: (err: Error) => toast.error(err.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => invoke({ action: "delete", id }),
    onSuccess: () => {
      toast.success("Data source removed.");
      refetch();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return { connections, isLoading, create, update, toggle, remove, refetch };
}

// Inline test (pre-save) → calls data-source-fetch with { test: true }. Used by
// the setup wizard to validate the endpoint + auth before persisting.
export interface TestConnectionInput {
  endpoint_url: string;
  http_method: "GET" | "POST";
  auth_type: "none" | "bearer" | "header" | "basic" | "query";
  auth_credentials?: Record<string, string> | null;
  request_body_template?: unknown;
  args?: Record<string, unknown>;
}

export interface TestConnectionResult {
  ok: boolean;
  status?: number;
  data?: unknown;
  bytes?: number;
  ms?: number;
  error?: string;
}

export async function testBusinessDataConnection(input: TestConnectionInput): Promise<TestConnectionResult> {
  const { data, error } = await supabase.functions.invoke("data-source-fetch", {
    body: { test: true, ...input },
  });
  if (error) return { ok: false, error: await readEdgeError(error, "Request failed") };
  return data as TestConnectionResult;
}

// MCP test (pre-save) → calls mcp-fetch with { test: true, op: "list" } to
// handshake the remote MCP server and discover its tools. Proves connectivity
// + auth and shows the owner what the AI will be able to do.
export interface McpTestTool {
  name: string;
  description?: string;
  annotations?: { readOnlyHint?: boolean };
}
export interface McpTestResult {
  ok: boolean;
  tools?: McpTestTool[];
  error?: string;
}

export async function testMcpConnection(input: {
  endpoint_url: string;
  auth_type: "none" | "bearer" | "header" | "basic" | "query";
  auth_credentials?: Record<string, string> | null;
}): Promise<McpTestResult> {
  const { data, error } = await supabase.functions.invoke("mcp-fetch", {
    body: { test: true, op: "list", ...input },
  });
  if (error) return { ok: false, error: await readEdgeError(error, "Request failed") };
  return data as McpTestResult;
}

// Trigger a fresh fetch for an existing url_feed connection (refresh button).
export async function refreshBusinessDataConnection(connectionId: string): Promise<TestConnectionResult> {
  const { data, error } = await supabase.functions.invoke("data-source-fetch", {
    body: { connection_id: connectionId },
  });
  if (error) return { ok: false, error: await readEdgeError(error, "Request failed") };
  return data as TestConnectionResult;
}
